import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CACHE_KEY_DEPARTMENTS } from "../../../utils/constants";
import { DepartmentInterface } from "../../../models/TicketModule/Department";
import departmentService from "../../../services/ticketService/departmentService";

interface UpdateDepartmentsContext {
  previousDepartments: DepartmentInterface[];
}

const useUpdateDepartment = (page: number, pageSize: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    DepartmentInterface,
    Error,
    DepartmentInterface,
    UpdateDepartmentsContext
  >({
    mutationFn: async (department: DepartmentInterface) => {
      // Assuming the ID is stored in the department object or can be derived
      const { id, ...departmentData } = department;
      return departmentService.Update(id || "", departmentData);
    },

    onMutate: async (updatedDepartment: DepartmentInterface) => {
      await queryClient.cancelQueries({
        queryKey: [
          CACHE_KEY_DEPARTMENTS,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
      });

      const previousDepartments =
        queryClient.getQueryData<DepartmentInterface[]>([
          CACHE_KEY_DEPARTMENTS,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ]) || [];

      queryClient.setQueryData<DepartmentInterface[]>(
        [
          CACHE_KEY_DEPARTMENTS,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        (departmentes = []) =>
          departmentes.map((department) =>
            department.id === updatedDepartment.id
              ? updatedDepartment
              : department
          )
      );

      return { previousDepartments };
    },

    onSuccess: (savedDepartment) => {
      queryClient.setQueryData<DepartmentInterface[]>(
        [
          CACHE_KEY_DEPARTMENTS,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        (departmentes) =>
          departmentes?.map((department) =>
            department.id === savedDepartment.id ? savedDepartment : department
          )
      );
    },

    onError: (error, updatedDepartment, context) => {
      if (!context) return;

      queryClient.setQueryData<DepartmentInterface[]>(
        [
          CACHE_KEY_DEPARTMENTS,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        context.previousDepartments
      );
    },
  });
};

export default useUpdateDepartment;
